import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { processSpriteSheet, type SpriteAtlas } from '../../lib/spriteProcess';

/**
 * Sprite Generator (35j). Pick a base template → describe a character (and/or add a reference) → NanoBanana
 * restyles the character across the template's grid in one pass. The studio keys / slices / normalises the
 * raw sheet in-canvas, previews the walk, and saves a clean atlas (sheet PNG + atlas JSON) into the project.
 */

interface SpriteTemplate {
  id: string;
  label: string;
  perspective: { id: string; label: string };
  grid: { cols: number; rows: number; cells: number };
  directions: string[];
  poses: string[];
  walkCycle: number[];
  idleFrame: number;
}

interface Processed {
  sheetDataUrl: string;
  atlas: SpriteAtlas;
}

function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.8 4.9L18.7 9.7 13.8 11.5 12 16.4 10.2 11.5 5.3 9.7 10.2 7.9z" />
    </svg>
  );
}

const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });

/** Small looping walk preview: draws the chosen direction row, cycling the template's walk frames. */
function WalkPreview({ processed }: { processed: Processed }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dir, setDir] = useState(0);
  const [playing, setPlaying] = useState(true);
  const dirs = processed.atlas.directions;

  useEffect(() => {
    const img = new Image();
    img.onload = () => { imgRef.current = img; };
    img.src = processed.sheetDataUrl;
    return () => { imgRef.current = null; };
  }, [processed.sheetDataUrl]);

  useEffect(() => {
    let raf = 0;
    let step = 0;
    let last = performance.now();
    const { atlas } = processed;
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      const cv = canvasRef.current;
      const img = imgRef.current;
      if (!cv || !img) return;
      if (playing && now - last > 130) { last = now; step = (step + 1) % atlas.walkCycle.length; }
      const col = playing ? atlas.walkCycle[step] : atlas.idleFrame;
      const frame = atlas.frames.find(f => f.row === dir && f.col === col) ?? atlas.frames[dir * atlas.cols];
      const ctx = cv.getContext('2d')!;
      ctx.clearRect(0, 0, cv.width, cv.height);
      // checker ground
      const T = 14;
      for (let y = 0; y < cv.height; y += T) for (let x = 0; x < cv.width; x += T) {
        ctx.fillStyle = ((x / T + y / T) & 1) ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.09)';
        ctx.fillRect(x, y, T, T);
      }
      if (!frame) return;
      const scale = Math.min((cv.width * 0.7) / frame.w, (cv.height * 0.82) / frame.h);
      const dw = frame.w * scale;
      const dh = frame.h * scale;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, frame.x, frame.y, frame.w, frame.h, (cv.width - dw) / 2, cv.height - 10 - dh, dw, dh);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [processed, dir, playing]);

  return (
    <div className="rounded-2xl border border-line bg-page/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold text-accent-deep">Walk preview</p>
        <button
          type="button"
          onClick={() => setPlaying(p => !p)}
          className="rounded-lg border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-ink transition hover:border-accent/50"
        >
          {playing ? 'Pause' : 'Play'}
        </button>
      </div>
      <canvas ref={canvasRef} width={220} height={180} className="mb-2 block w-full rounded-xl border border-line bg-surface" />
      <div className="flex flex-wrap gap-1.5">
        {dirs.map((dname, i) => (
          <button
            key={dname + i}
            type="button"
            onClick={() => setDir(i)}
            className={[
              'rounded-lg border px-2 py-1 text-[11px] font-semibold capitalize transition',
              i === dir ? 'border-accent bg-accent/10 text-accent-deep' : 'border-line bg-surface text-muted hover:text-ink',
            ].join(' ')}
          >
            {dname}
          </button>
        ))}
      </div>
    </div>
  );
}

interface SpritePanelProps {
  projectId: string;
  onSaved: () => void;
}

export function SpritePanel({ projectId, onSaved }: SpritePanelProps) {
  const [templates, setTemplates] = useState<SpriteTemplate[] | null>(null);
  const [templateId, setTemplateId] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [referenceDataUrl, setReferenceDataUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [processed, setProcessed] = useState<Processed | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ templates: SpriteTemplate[] }>(`/box/projects/${projectId}/media/sprites/templates`);
      setTemplates(data.templates);
      if (data.templates[0]) setTemplateId(data.templates[0].id);
    } catch {
      setStatus('Could not load sprite templates');
      setTemplates([]);
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  const template = (templates ?? []).find(t => t.id === templateId) ?? null;

  const generate = async () => {
    if (!template || generating || (!prompt.trim() && !referenceDataUrl)) return;
    setGenerating(true);
    setStatus(null);
    setProcessed(null);
    try {
      const { sheet } = await api.post<{ sheet: string }>(`/box/projects/${projectId}/media/sprites/generate`, {
        templateId,
        prompt: prompt.trim(),
        referenceDataUrl: referenceDataUrl ?? undefined,
      });
      setStatus('Cleaning up the sheet…');
      const result = await processSpriteSheet(sheet, {
        cols: template.grid.cols,
        rows: template.grid.rows,
        directions: template.directions,
        poses: template.poses,
        walkCycle: template.walkCycle,
        idleFrame: template.idleFrame,
      });
      setProcessed(result);
      if (!name.trim()) setName((prompt.trim().split(/\s+/).slice(0, 3).join('-') || 'sprite').toLowerCase().replace(/[^a-z0-9_-]/g, ''));
      setStatus(null);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not generate that sprite');
    } finally {
      setGenerating(false);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setStatus('That reference is too big — 5MB limit'); return; }
    try {
      setReferenceDataUrl(await readDataUrl(file));
    } catch {
      setStatus('Could not read that reference image');
    }
  };

  const save = async () => {
    if (!processed || saving) return;
    const safe = name.trim().replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
    if (!safe) { setStatus('Give the sprite a short name'); return; }
    setSaving(true);
    setStatus(null);
    try {
      await api.post(`/box/projects/${projectId}/media/sprites`, {
        name: safe,
        pngDataUrl: processed.sheetDataUrl,
        atlas: { ...processed.atlas, name: safe },
      });
      setStatus(`Saved "${safe}" to your project assets.`);
      onSaved();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not save the sprite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {/* template picker */}
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted">Base template</p>
        {templates === null ? (
          <div className="mb-3 h-16 animate-pulse rounded-xl bg-page" />
        ) : (
          <div className="mb-3 grid gap-2">
            {templates.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTemplateId(t.id)}
                className={[
                  'flex items-center justify-between rounded-xl border-2 px-3 py-2.5 text-left transition',
                  t.id === templateId ? 'border-accent ring-2 ring-accent/25' : 'border-line hover:border-accent/50',
                ].join(' ')}
              >
                <span>
                  <span className="block text-sm font-bold text-ink">{t.label}</span>
                  <span className="block text-[11px] text-muted">{t.perspective.label} · {t.grid.cells} frames · {t.directions.length} directions</span>
                </span>
                {t.id === templateId && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-accent-deep">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}

        {/* character prompt + reference */}
        <div className="mb-3 rounded-2xl border border-line bg-page/50 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-accent-deep"><SparkleIcon /> Describe the character</p>
          <textarea
            rows={3}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g. a knight in silver armor with a blue cape and a small shield"
            className="mb-2 block w-full resize-none rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent/60"
          />
          <div className="mb-2 flex items-center gap-2">
            {referenceDataUrl ? (
              <div className="flex items-center gap-2">
                <img src={referenceDataUrl} alt="reference" className="h-9 w-9 rounded-lg border border-line object-cover" />
                <button type="button" onClick={() => setReferenceDataUrl(null)} className="text-[11px] font-semibold text-muted hover:text-danger">Remove</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="rounded-lg border border-dashed border-line px-2.5 py-1.5 text-[11px] font-semibold text-muted transition hover:border-accent/50 hover:text-ink"
              >
                + Reference image (optional)
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => void generate()}
            disabled={!template || generating || (!prompt.trim() && !referenceDataUrl)}
            className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-bold text-accent-ink transition hover:opacity-90 disabled:opacity-50"
          >
            {generating ? 'Generating…' : 'Generate sprite'}
          </button>
        </div>

        {/* result: preview + save */}
        {processed && (
          <div className="grid gap-3">
            <WalkPreview processed={processed} />
            <div className="rounded-2xl border border-line bg-page/50 p-3">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted">Save to project</p>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="sprite name"
                className="mb-2 block w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent/60"
              />
              <p className="mb-2 text-[11px] text-muted">Saves a sheet + atlas your game can load with <code className="rounded bg-page px-1">CodeBox.assets</code>.</p>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || !name.trim()}
                className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-bold text-accent-ink transition hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Add to project assets'}
              </button>
            </div>
          </div>
        )}
      </div>

      {status && <p className="shrink-0 border-t border-line px-3 py-2 text-xs text-muted">{status}</p>}
    </div>
  );
}
